import { Product, Variant } from '~/types/product';
import removeSpecialCharacters from './regex';
import { COLOR, SIZE } from '../constants/product';

export const basePrices: { [key: string]: string } = {
  'T-Shirt': '35.0',
  Hoodie: '65.0',
  'Long Sleeve': '45.0',
  'Tote Bag': '20.0',
  Sunglasses: '40.0',
  'Shopping Bag': '25.0',
  Sweatpants: '55.0',
  Crewneck: '55.0',
  Mask: '10.0',
};

export const getColors = (size: string, variants: Variant[]) => {
  return variants.reduce(
    (accumulator: Array<{ color: string; available: boolean }>, item) => {
      const color = item.options.find((opt) => opt.name === 'Color')?.value;

      if (!accumulator.some((item) => item.color === color) && color) {
        const available = variants.some((variant) => {
          const colorVariant = variant.options.find(
            (opt) => opt.name === COLOR,
          )?.value;
          const sizeVariant = variant.options.find(
            (opt) => opt.name === SIZE,
          )?.value;

          return (
            colorVariant === color &&
            sizeVariant === size &&
            variant.availableForSale
          );
        });

        accumulator.push({ color, available });
      }
      return accumulator;
    },
    [],
  );
};

export const getSizes = (color: string, variants: Variant[]) => {
  return variants.reduce(
    (accumulator: Array<{ size: string; available: boolean }>, item) => {
      const colorVariant = item.options.find(({ name }) => name === COLOR);
      const sizeVariant = item.options.find(({ name }) => name === SIZE);

      if (colorVariant?.value === color && sizeVariant?.value) {
        if (!accumulator.some(({ size }) => size === sizeVariant?.value))
          accumulator.push({
            size: sizeVariant?.value,
            available: item.availableForSale,
          });
      }
      return accumulator;
    },
    [],
  );
};

export const oneSizeVariantPredicate = (variant: Variant, color: string) =>
  variant.options[0].value === color;

export const multiSizeVariantPredicate = (
  variant: Variant,
  color: string,
  size: string,
) => variant.options[0].value === size && variant.options[1].value === color;

export const getVariantId = (
  variants: Variant[],
  color: string,
  size: string,
): string => {
  return (
    variants.find((variant) => {
      if (variant.options.length > 1) {
        return multiSizeVariantPredicate(variant, color, size);
      }
      return oneSizeVariantPredicate(variant, color);
    })?.id || ''
  );
};

export const isAvailable = (
  color: string,
  variants: Array<Variant>,
): boolean => {
  const available = !!variants.find(
    (variant) =>
      variant.options.find((opt) => opt.name === 'Color')?.value === color &&
      variant.availableForSale,
  );
  return available;
};

export const isOneVariantAvailable = (variants: Variant[]): boolean => {
  return variants.some((variant) => variant.availableForSale === true);
};

export const isRaffleProduct = (product: Product) =>
  product?.title.toLowerCase().includes('raffle');

export const isMysteryProduct = (product: Product) =>
  product?.title.toLowerCase().includes('mystery');

export const getProductsByHandle = (
  products: Array<Product>,
  title: string,
) => {
  if (!products) return [];

  return products.filter((product) => {
    const productHandle = removeSpecialCharacters(product.handle);
    const handle = removeSpecialCharacters(title);

    return productHandle === handle;
  });
};

export const getProductImages = (
  product: Product | null,
  products: Array<Product>,
  selectedColor: string,
) => {
  const options = product?.variants[0]?.options;
  const optionIndex = options && options.length > 1 ? 1 : 0;

  return products.find(
    (item) => item.variants[0]?.options[optionIndex]?.value === selectedColor,
  )?.images;
};

export const getProductVariants = (products: Array<Product>) => {
  return products.reduce((acc: Variant[] = [], value) => {
    acc.push(...value.variants);
    return acc;
  }, []);
};

export const getAvailableSizes = (product: Product) => {
  return product.variants.reduce(
    (accumulator: Array<{ size: string; available: boolean }>, item) => {
      if (!item) return accumulator;

      const currentSize =
        item?.options?.find(({ name }) => name === SIZE)?.value || '';

      if (!accumulator.some(({ size }) => size === currentSize)) {
        accumulator.push({
          size: currentSize,
          available: item.availableForSale,
        });
      }
      return accumulator;
    },
    [],
  );
};
